import { useCallback, useSyncExternalStore } from "react";
import { z } from "zod";
import { FlagApi } from "@/lib/api/flags";
import { StreamApi } from "@/lib/api/stream";
import type { Data } from "./data";

const flagSchema = z.object({
  id: z.string(),
  config: z.object({
    value: z.boolean(),
    // Optional per-vessel overrides keyed by vessel id. A present entry wins
    // over the global `value`; absent entries fall back to it. Lets a flag be
    // rolled out (or held back) one vessel at a time.
    vessels: z.record(z.string(), z.boolean()).optional(),
  }),
});

type FlagConfig = z.infer<typeof flagSchema>["config"];

const DISABLED: FlagConfig = { value: false };

// Transient failures (network blip during boot, PocketBase restarting mid-deploy,
// expired token awaiting re-login) are retried with backoff instead of being
// treated as "flag off". The cap keeps retrying once a minute so a flag always
// recovers once connectivity or auth comes back, without a page reload.
const RETRY_DELAYS_MS = [1_000, 5_000, 15_000, 60_000];

type FlagListener = (config: FlagConfig) => void;

type CacheEntry = {
  config: FlagConfig;
  listeners: Set<FlagListener>;
  unsubscribe?: () => void;
  retryTimer?: ReturnType<typeof setTimeout>;
  attempt: number;
  // True while a fetch/subscribe loop is running or scheduled for this entry.
  active: boolean;
  // Bumped when the last listener releases; in-flight continuations compare
  // against the generation they started with and discard themselves if stale.
  generation: number;
};

// One entry per flag so many components share a single fetch+subscription.
// Entries are never removed: releasing the last listener tears down the
// subscription and any pending retry, but keeps the last-known config so the
// next mount renders warm instead of flashing "off" while refetching.
const subscriptionCache = new Map<string, CacheEntry>();

function getEntry(flagId: string): CacheEntry {
  let entry = subscriptionCache.get(flagId);
  if (!entry) {
    entry = { config: DISABLED, listeners: new Set(), attempt: 0, active: false, generation: 0 };
    subscriptionCache.set(flagId, entry);
  }
  return entry;
}

function notify(entry: CacheEntry, config: FlagConfig) {
  entry.config = config;
  entry.listeners.forEach((listener) => {
    try {
      listener(config);
    } catch (error) {
      console.error("Feature flag listener error:", error);
    }
  });
}

function applyRecord(flagId: string, entry: CacheEntry, record: object) {
  const parseResult = flagSchema.safeParse(record);
  if (!parseResult.success) {
    console.warn(`Feature flag ${flagId} has invalid schema.`);
    return;
  }
  notify(entry, parseResult.data.config);
}

function scheduleRetry(flagId: string, entry: CacheEntry, generation: number) {
  if (entry.generation !== generation) return;
  const delay = RETRY_DELAYS_MS[Math.min(entry.attempt, RETRY_DELAYS_MS.length - 1)];
  entry.attempt += 1;
  entry.retryTimer = setTimeout(() => {
    entry.retryTimer = undefined;
    void fetchAndSubscribe(flagId, entry, generation);
  }, delay);
}

// Re-read a flag's current config without touching the subscription — used
// when the stream reconnects after missing messages.
async function applyLatest(flagId: string, entry: CacheEntry, generation: number) {
  try {
    const record = await FlagApi.fetchFlag<Data.FeatureFlag>(flagId);
    if (entry.generation !== generation) return;
    if (!record) {
      notify(entry, DISABLED);
      return;
    }
    applyRecord(flagId, entry, record);
  } catch (error) {
    console.debug(`Feature flag ${flagId} resync failed:`, error);
  }
}

async function fetchAndSubscribe(flagId: string, entry: CacheEntry, generation: number) {
  try {
    const record = await FlagApi.fetchFlag<Data.FeatureFlag>(flagId);
    if (entry.generation !== generation) return;
    if (!record) {
      // Definitive: the flag record does not exist → off, stop the loop (a
      // later mount restarts it, so a flag created afterwards is picked up on
      // the next navigation/page load).
      //
      // This used to be a 404 plus a pb.authStore.isValid guard, because the
      // collection API answered a missing record, a rule-blocked read AND an
      // expired token all with 404 — so "no such flag" and "not logged in"
      // were indistinguishable, and mistaking the second for the first would
      // have left every flag off until a page reload. /app/feature-flags/one
      // answers absence with a null body and failure with an error, so the
      // two paths are now the two branches below.
      console.debug(`Feature flag ${flagId} not found, treating as disabled.`);
      notify(entry, DISABLED);
      entry.active = false;
      return;
    }
    applyRecord(flagId, entry, record);
  } catch (error) {
    if (entry.generation !== generation) return;
    // Transient failure — keep the last known value and retry, instead of
    // fail-closing for the rest of the session.
    console.debug(`Feature flag ${flagId} fetch failed, retrying:`, error);
    scheduleRetry(flagId, entry, generation);
    return;
  }

  try {
    const unsubscribe = StreamApi.subscribe([StreamApi.featureFlagTopic(flagId)], {
      onChange: (change) => {
        if (entry.generation !== generation) return;
        if (change.action === "delete") {
          notify(entry, DISABLED);
          return;
        }
        applyRecord(flagId, entry, change.record as unknown as Data.FeatureFlag);
      },
      // A missed toggle would leave the UI gated on a stale value, so refetch
      // rather than wait for the next edit.
      onResync: () => {
        // Re-read the flag: a toggle missed while disconnected would otherwise
        // leave the UI gated on a stale value until the next edit.
        void applyLatest(flagId, entry, generation);
      },
    });
    if (entry.generation !== generation) {
      // Everyone unmounted while the subscribe call was in flight.
      try {
        unsubscribe();
      } catch (_unsubError) {
        // Silently ignore - global handler will catch these
      }
      return;
    }
    entry.unsubscribe = unsubscribe;
    // Full success — reset the backoff only now, so persistent subscribe
    // failures escalate 1s→60s instead of hammering at the 1s floor.
    entry.attempt = 0;
  } catch (subError) {
    // The fetched value is fine, but without a live subscription the flag
    // would go silently stale — retry until the subscription sticks.
    //
    // StreamApi.subscribe registers against the shared connection and returns
    // its own unsubscribe, so there is no orphaned listener to clean up here
    // the way the PocketBase SDK left one behind on a rejected subscribe.
    console.debug(`Feature flag ${flagId} subscription failed, retrying:`, subError);
    if (entry.generation !== generation) return;
    scheduleRetry(flagId, entry, generation);
  }
}

function releaseEntry(entry: CacheEntry) {
  entry.generation += 1;
  entry.active = false;
  if (entry.retryTimer) {
    clearTimeout(entry.retryTimer);
    entry.retryTimer = undefined;
  }
  if (entry.unsubscribe) {
    try {
      entry.unsubscribe();
    } catch (_unsubError) {
      // Silently ignore - global handler will catch these
    }
    entry.unsubscribe = undefined;
  }
  // entry.config is deliberately kept — see subscriptionCache comment.
}

// Connectivity returning is the common end of a retry window: fire pending
// retries immediately instead of waiting out the (up to 60s) backoff.
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("online", () => {
    for (const [flagId, entry] of subscriptionCache) {
      if (entry.retryTimer) {
        clearTimeout(entry.retryTimer);
        entry.retryTimer = undefined;
        void fetchAndSubscribe(flagId, entry, entry.generation);
      }
    }
  });
}

/** Last known config without subscribing (initial render state). */
export function peekFlag(flagId: string): FlagConfig {
  return subscriptionCache.get(flagId)?.config ?? DISABLED;
}

/**
 * Register a listener for a flag, starting the shared fetch+realtime
 * subscription on first use. The listener is called immediately with the last
 * known config, then on every change. Returns a release function; when the
 * last listener releases, the realtime subscription and any pending retry are
 * torn down (the last-known config stays cached for the next mount).
 */
export function acquireFlag(flagId: string, listener: FlagListener): () => void {
  const entry = getEntry(flagId);
  entry.listeners.add(listener);
  try {
    listener(entry.config);
  } catch (error) {
    console.error("Feature flag listener error:", error);
  }

  if (!entry.active) {
    entry.active = true;
    entry.attempt = 0;
    void fetchAndSubscribe(flagId, entry, entry.generation);
  }

  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size === 0) {
      releaseEntry(entry);
    }
  };
}

function useFeatureFlagConfig(flagId: string): FlagConfig {
  const subscribe = useCallback((onStoreChange: () => void) => acquireFlag(flagId, onStoreChange), [flagId]);
  return useSyncExternalStore(
    subscribe,
    useCallback(() => peekFlag(flagId), [flagId]),
  );
}

/** Global on/off state of a feature flag. */
export function useFeatureFlag(flagId: string): boolean {
  return useFeatureFlagConfig(flagId).value;
}

/**
 * Feature flag state resolved for a specific vessel: a per-vessel override in
 * `config.vessels` wins over the global `config.value`, otherwise the global
 * value applies. Use for progressively rolling a flag out per vessel.
 */
export function useVesselFeatureFlag(flagId: string, vesselId: string | null | undefined): boolean {
  return resolveVesselFlag(useFeatureFlagConfig(flagId), vesselId);
}

export function resolveVesselFlag(
  config: { value: boolean; vessels?: Record<string, boolean> },
  vesselId: string | null | undefined,
): boolean {
  const override = vesselId ? config.vessels?.[vesselId] : undefined;
  return override ?? config.value;
}
